import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { useState } from "react";

const Contact = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);
    setSubmitError(null);

    const form = event.currentTarget;
    const formData = new FormData(form);

    // Web3Forms access key
    const accessKey = "7a5f25d4-a605-4dfc-b7c5-0a2c5b4392f7";

    const payload: Record<string, unknown> = {
      access_key: accessKey,
      subject: "New Contact Form Submission - Avoca Veterinary",
    };

    // Copy input fields into payload
    for (const [key, value] of formData.entries()) {
      if (key === "access_key") continue;
      payload[key] = value;
    }

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setSubmitMessage(
          "Message sent successfully! We'll be in touch shortly."
        );
        form.reset();
      } else {
        setSubmitError("Failed to send message. Please try again.");
      }
    } catch (err) {
      setSubmitError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Contact Us
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Get in touch to discuss your pet's care, ask questions, or
              schedule a consultation. We're here to help you through this
              difficult time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section id="contact-form" className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="card"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-primary-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Send us a Message
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you as soon as
                  possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot for spam bots */}
                <input
                  type="checkbox"
                  name="botcheck"
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="pet_name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Pet's Name
                  </label>
                  <input
                    type="text"
                    id="pet_name"
                    name="pet_name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Your pet's name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Service Required
                  </label>
                  <select
                    id="service"
                    name="service"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  >
                    <option value="">Select a service</option>
                    <option value="home_euthanasia">Home Euthanasia</option>
                    <option value="quality_of_life_assessment">
                      Quality of Life Assessment
                    </option>
                    <option value="aftercare_consultation">
                      Aftercare Consultation
                    </option>
                    <option value="general_inquiry">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Please tell us about your situation and how we can help..."
                  ></textarea>
                </div>

                <div>
                  <label
                    htmlFor="preferred_contact"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Preferred Contact Method
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="phone"
                        defaultChecked
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">Phone</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="email"
                        className="mr-2 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">Email</span>
                    </label>
                  </div>
                </div>

                {submitMessage && (
                  <div className="rounded-md bg-green-50 p-4 text-green-800 text-sm">
                    {submitMessage}
                  </div>
                )}
                {submitError && (
                  <div className="rounded-md bg-red-50 p-4 text-red-800 text-sm">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary inline-flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Direct Contact */}
              <div className="card">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Get in Touch
                </h3>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Phone</h4>
                      <a
                        href="tel:+447850446017"
                        className="text-primary-600 hover:text-primary-700 transition-colors duration-200"
                      >
                        +447850446017
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Email</h4>
                      <a
                        href="mailto:info@denisvet.co.uk"
                        className="text-primary-600 hover:text-primary-700 transition-colors duration-200"
                      >
                        avocavet@proton.me
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <MapPin className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900">
                        Service Area
                      </h4>
                      <p className="text-gray-600">
                        East Yorkshire, covering most of Yorkshire and North
                        Lincolnshire
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Availability */}
              <div className="card">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Availability
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-secondary-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Standard Hours
                      </h4>
                      <p className="text-gray-600">8am to 9pm</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Emergency Hours
                      </h4>
                      <p className="text-gray-600">
                        9pm to 8am (24/7 availability)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="card">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Response Time
                </h3>
                <div className="space-y-4">
                  <div className="bg-primary-50 rounded-lg p-4">
                    <p className="text-primary-800 text-sm">
                      <strong>Phone calls:</strong> We aim to answer immediately
                      or return calls within 1 hour
                    </p>
                  </div>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <p className="text-secondary-800 text-sm">
                      <strong>Email inquiries:</strong> We respond to all emails
                      within 4 hours during business hours
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-yellow-800 text-sm">
                      <strong>Emergency situations:</strong> Immediate response
                      and same-day service when possible
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Area Map */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Service Coverage Area
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Based in East Yorkshire, we serve families across the region with
              compassionate home visit services.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: "East Yorkshire",
                description:
                  "Primary service area including Hull, Beverley, and surrounding areas",
              },
              {
                name: "North Yorkshire",
                description:
                  "Covering York, Harrogate, and North Yorkshire countryside",
              },
              {
                name: "West Yorkshire",
                description:
                  "Serving Leeds, Bradford, and West Yorkshire communities",
              },
              {
                name: "South Yorkshire",
                description:
                  "Covering Sheffield, Doncaster, and South Yorkshire areas",
              },
              {
                name: "North Lincolnshire",
                description:
                  "Serving Scunthorpe, Grimsby, and North Lincolnshire",
              },
              {
                name: "Additional Areas",
                description:
                  "Contact us to discuss coverage for other locations",
              },
              {
                name: "Emergency Coverage",
                description: "24/7 emergency services across all covered areas",
              },
              {
                name: "Travel Fees",
                description:
                  "Additional travel fees may apply for extended distances",
              },
            ].map((area, index) => (
              <motion.div
                key={area.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors duration-200"
              >
                <h3 className="font-semibold text-gray-900 mb-2">
                  {area.name}
                </h3>
                <p className="text-gray-600 text-sm">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
